import React, { useState, useEffect } from "react";
import T from './common/T';
import { Dialog, Transition } from "@headlessui/react"; // If you don't have headlessui, use standard div/css
import { XMarkIcon, CloudArrowUpIcon, DocumentIcon } from "@heroicons/react/24/outline";
import { useDocuments } from "../context/DocumentContext";
import { toast } from "react-toastify";

// Note: If you don't use HeadlessUI, simple conditional rendering works too.
// This example uses standard Tailwind CSS for a custom modal to keep it dependency-free.

const DocumentUploadModal = ({ isOpen, onClose, projectId, prefillProjectName }) => {
  const { addDocument } = useDocuments();
  const [loading, setLoading] = useState(false);

  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    documentName: "",
    documentType: "drawing",
    description: "",
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setFormData({ documentName: "", documentType: "drawing", description: "" });
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setFile(selected);
      // Auto-fill name if empty
      if (!formData.documentName) {
        setFormData(prev => ({ ...prev, documentName: selected.name }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !formData.documentName) return toast.error("File and Name are required");

    setLoading(true);
    const payload = new FormData();
    payload.append("file", file);
    payload.append("documentName", formData.documentName);
    payload.append("documentType", formData.documentType);
    payload.append("description", formData.description);
    
    // 🟢 AUTO-LINK TO THE CURRENT PROJECT
    if (projectId) payload.append("projectId", projectId);
    
    // Dummy User ID (Replace with real auth later)
    payload.append("userId", 1); 

    try {
      await addDocument(payload);
      toast.success("Document Uploaded!");
      onClose(); // Close Modal
    } catch (err) {
      toast.error("Upload Failed");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        
        {/* Header */}
        <div className="bg-gray-900 px-6 py-4 flex justify-between items-center text-white">
          <h3 className="text-base font-bold flex items-center gap-2">
            <CloudArrowUpIcon className="w-5 h-5" /> Upload Document
          </h3>
          <button onClick={onClose} className="hover:text-gray-300">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          
          {/* Project Badge (Read Only) */}
          {prefillProjectName && (
             <div className="bg-blue-50 text-blue-700 px-3 py-2 rounded text-sm font-medium border border-blue-100 inline-block">
                📂 Linking to: {prefillProjectName}
             </div>
          )}

          {/* File Input */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 transition cursor-pointer relative">
            <input 
                type="file" 
                onChange={handleFileChange} 
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
            />
            <div className="flex flex-col items-center pointer-events-none">
                {file ? (
                    <>
                        <DocumentIcon className="w-10 h-10 text-green-500 mb-2" />
                        <span className="text-gray-900 font-medium truncate max-w-xs">{file.name}</span>
                        <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                    </>
                ) : (
                    <>
                        <CloudArrowUpIcon className="w-10 h-10 text-gray-400 mb-2" />
                        <span className="text-blue-600 font-medium"><T>Click to select file</T></span>
                        <span className="text-xs text-gray-400"><T>PDF, DWG, PNG, JPG</T></span>
                    </>
                )}
            </div>
          </div>

          {/* Document Name */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase mb-1"><T>Document Name </T><span className="text-red-500">*</span></label>
            <input 
                type="text" 
                className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                value={formData.documentName}
                onChange={(e) => setFormData({...formData, documentName: e.target.value})}
                placeholder="e.g. Foundation Blueprint v1"
                required
            />
          </div>

          {/* Document Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1"><T>Type</T></label>
                <select 
                    className="w-full border border-gray-300 p-2 rounded bg-white outline-none"
                    value={formData.documentType}
                    onChange={(e) => setFormData({...formData, documentType: e.target.value})}
                >
                    <option value="drawing"><T>Drawing</T></option>
                    <option value="contract"><T>Contract</T></option>
                    <option value="invoice"><T>Invoice</T></option>
                    <option value="permit"><T>Permit</T></option>
                    <option value="manual"><T>Manual</T></option>
                    <option value="other"><T>Other</T></option>
                </select>
            </div>
            
            {/* Description / Notes */}
            <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1"><T>Notes</T></label>
                <input 
                    type="text" 
                    className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="Brief description..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border rounded text-gray-600 hover:bg-gray-50 font-medium">
                Cancel
            </button>
            <button 
                type="submit" 
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 shadow-lg disabled:opacity-50"
            >
                {loading ? "Uploading..." : "Upload"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default DocumentUploadModal;